import { useState, useCallback, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { getRoles } from '@/auth/permissions'
import { useTenants } from '@/api/admin'

/**
 * useTenant manages the "active tenant" context.
 *
 * Loads tenants from API and tracks the active tenant by UUID (required by the API).
 * Falls back to slug matching for users whose tokens carry group claims.
 */
export function useTenant() {
  const { user } = useAuth()
  const isAdmin = user ? getRoles(user).includes('platform-admin') : false

  // Always load from API — backend filters by user visibility
  const { data: tenantsData } = useTenants()
  const tenants = tenantsData?.data ?? []

  const [activeTenantId, setActiveTenantId] = useState<string | null>(null)

  // Auto-select first tenant once loaded, and re-select if the active one was deleted
  useEffect(() => {
    if (tenants.length === 0) return
    const stillExists = tenants.some(t => t.id === activeTenantId)
    if (!stillExists) {
      setActiveTenantId(tenants[0].id)
    }
  }, [tenants, activeTenantId])

  const switchTenant = useCallback((id: string) => {
    setActiveTenantId(id)
  }, [])

  const activeTenant = tenants.find(t => t.id === activeTenantId) ?? null

  return {
    activeTenantId,
    activeTenantSlug: activeTenant?.slug ?? null,  // kept for display only
    switchTenant,
    tenants,
    isAdmin,
  }
}
